import React from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "../../utils/useScrollAnimation";

function FeatureComparison() {
  const { ref, isInView } = useScrollAnimation();
  
  const featureCategories = [
    {
      name: "Core Features",
      features: [
        {
          name: "Email Processing",
          free: "100 emails/month",
          pro: "Unlimited",
          enterprise: "Unlimited"
        },
        {
          name: "Email Categorization",
          free: "Basic (5 categories)",
          pro: "Advanced (unlimited)",
          enterprise: "Advanced (unlimited)"
        },
        {
          name: "Email Summary",
          free: "Weekly",
          pro: "Daily",
          enterprise: "Real-time"
        },
        {
          name: "Bulk Actions",
          free: "Basic",
          pro: "Advanced with rules",
          enterprise: "Advanced with automation"
        }
      ]
    },
    {
      name: "AI Features",
      features: [
        {
          name: "Smart Reply",
          free: "Limited",
          pro: "Full access",
          enterprise: "Customizable templates"
        },
        {
          name: "Email Summarization",
          free: "Basic",
          pro: "Advanced",
          enterprise: "Advanced + custom rules"
        },
        {
          name: "Priority Detection",
          free: "Basic",
          pro: "Advanced",
          enterprise: "Custom priority rules"
        },
        {
          name: "Sentiment Analysis",
          free: "—",
          pro: "✓",
          enterprise: "Advanced with insights"
        }
      ]
    },
    {
      name: "Management Features",
      features: [
        {
          name: "Attachment Management",
          free: "—",
          pro: "✓",
          enterprise: "Advanced organization"
        },
        {
          name: "Schedule Cleanup",
          free: "—",
          pro: "✓",
          enterprise: "✓"
        },
        {
          name: "Analytics Dashboard",
          free: "—",
          pro: "Basic",
          enterprise: "Advanced"
        },
        {
          name: "Team Collaboration",
          free: "—",
          pro: "—",
          enterprise: "✓"
        }
      ]
    },
    {
      name: "Support & Security",
      features: [
        {
          name: "Customer Support",
          free: "Community",
          pro: "Priority email",
          enterprise: "Dedicated account manager"
        },
        {
          name: "API Access",
          free: "—",
          pro: "Limited",
          enterprise: "Full access"
        },
        {
          name: "Custom Domain",
          free: "—",
          pro: "—",
          enterprise: "✓"
        },
        {
          name: "SLA",
          free: "—",
          pro: "—",
          enterprise: "99.9% uptime"
        }
      ]
    }
  ];

  return (
    <section ref={ref} className="py-20 px-4 bg-[#fff5eb]">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-[#694e42]">Compare Plan Features</h2>
          <p className="text-[#8a7266] max-w-3xl mx-auto">
            A detailed breakdown of what's included in each plan to help you make the right choice for your needs.
          </p>
        </motion.div>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#e2dad3]">
                <th className="text-left p-4 text-[#694e42] w-1/4">Features</th>
                <th className="p-4 text-[#694e42] w-1/4">
                  <div className="text-xl font-bold">Free</div>
                  <div className="text-[#8a7266] text-sm">$0/month</div>
                </th>
                <th className="p-4 text-[#694e42] bg-white rounded-t-xl border-t-2 border-l-2 border-r-2 border-[#ff8c42]/30 w-1/4">
                  <div className="text-xl font-bold text-[#ff8c42]">Pro</div>
                  <div className="text-[#8a7266] text-sm">$9.99/month</div>
                </th>
                <th className="p-4 text-[#694e42] w-1/4">
                  <div className="text-xl font-bold">Enterprise</div>
                  <div className="text-[#8a7266] text-sm">$19.99/month</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {featureCategories.map((category, categoryIndex) => (
                <React.Fragment key={category.name}>
                  <tr className="bg-[#f9f6f4]">
                    <td colSpan={4} className="p-4 font-bold text-[#694e42]">{category.name}</td>
                  </tr>
                  {category.features.map((feature, featureIndex) => (
                    <motion.tr 
                      key={`${categoryIndex}-${featureIndex}`} 
                      className="border-b border-[#e2dad3]"
                      initial={{ opacity: 0, y: 10 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.4, delay: (categoryIndex * 0.1) + (featureIndex * 0.05) }}
                    >
                      <td className="p-4 text-[#694e42]">{feature.name}</td>
                      <td className="p-4 text-center text-[#8a7266]">{feature.free}</td>
                      <td className="p-4 text-center bg-white border-l-2 border-r-2 border-[#ff8c42]/30 text-[#694e42] font-medium">
                        {feature.pro}
                      </td>
                      <td className="p-4 text-center text-[#8a7266]">{feature.enterprise}</td>
                    </motion.tr>
                  ))}
                </React.Fragment>
              ))}
              <tr>
                <td className="p-4"></td>
                <td className="p-4 text-center">
                  <motion.button
                    className="px-4 py-2 border border-[#694e42] text-[#694e42] rounded-md font-medium text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Get Started Free
                  </motion.button>
                </td>
                <td className="p-4 text-center bg-white border-b-2 border-l-2 border-r-2 border-[#ff8c42]/30 rounded-b-xl">
                  <motion.button
                    className="px-4 py-2 bg-[#ff8c42] text-white rounded-md font-medium text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Start 14-Day Trial
                  </motion.button>
                </td>
                <td className="p-4 text-center">
                  <motion.button
                    className="px-4 py-2 border border-[#694e42] text-[#694e42] rounded-md font-medium text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Contact Sales
                  </motion.button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default FeatureComparison;